import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { APP_INIT_RESPONSE_TYPE, BUTTON_TYPE, DOMAIN_NAMES } from '../../../global/constants';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { ROUTES } from '../../../routes/urls';
import { login, validateIsLoggedIn } from '../../../services/authServices';
import Button from '../../../component/button';
import { trackAnalyticActivity, trackPage } from '../../../services/analyticsService';
import { captureException } from '../../../services/logService';
import { CustomWindow } from '../../../interfaces/OneSignalInterface';
import AppLogo from '../../../component/appLogo';
import {
  Group,
  Content,
  TopView,
  Icon,
  Title,
  Form,
  FormBlock,
  InputValue,
  FormViewLogin,
  Viewer,
  FormText,
  FormTextPoint,
  FormSection,
  DividerRow,
  DividerLine,
  DividerText
} from '../styles';
import { VIEW_ANALYTICS } from '../../../global/analyticsConstants';
import LayoutWithoutNavbar from '../../../component/layoutWithoutNavbar';
import { useMobileDevice } from '../../../global/useMobile';
import GoogleButton from '../../../component/googleButton';
import { getCurrentDomain, isReactNative } from '../../../helpers/common';
import { Google } from '../../../interfaces/GoogleInterface';

declare const window: CustomWindow & {
  google?: Google;
  posthog?: {
    group: (type: string, id: string, properties: Record<string, unknown>) => void;
  };
};

const Login: React.FC = () => {
  //state
  const [username, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  //Other variable
  const currentDomain = getCurrentDomain();
  let history = useHistory();
  const locationSearch = useLocation().search;
  const isFromLogoutPage = new URLSearchParams(locationSearch).get('logout');
  const isMobile = useMobileDevice();

  useEffect(() => {
    trackPage({ visit_url: 'login' });
    trackAnalyticActivity(VIEW_ANALYTICS.LOGIN_PAGE);
    if (!isFromLogoutPage && validateIsLoggedIn()) {
      history.push(ROUTES.INBOX);
    }
  }, [history, isFromLogoutPage]);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      try {
        const payload = {
          username,
          password,
          appName: 'WebAppProject'
        };
        setLoading(true);
        const result = await login(payload);
        if (result && result.type === APP_INIT_RESPONSE_TYPE.REDIRECT && result.path) {
          window.OneSignal?.push(function () {
            window.OneSignal?.setSubscription(true);
          });
          history.push(result.path);
        } else {
          let organization = UserPreferenceSingleton.getInstance().getWorkspace();
          if (organization?.id) {
            const intercomPayload = {
              company: {
                name: organization?.name,
                id: organization?.id
              }
            };
            if (window?.posthog) {
              window.posthog.group('Workspace', intercomPayload.company?.id, {
                name: intercomPayload.company?.name
              });
            }
            const returnUrl = new URLSearchParams(locationSearch).get('return-url');
            if (returnUrl) history.push(decodeURIComponent(returnUrl));
            else history.push(ROUTES.INBOX);
          }
        }
      } catch (e) {
        captureException(e);
      } finally {
        setLoading(false);
      }
    },
    [password, username, history, locationSearch]
  );

  return (
    <>
      <LayoutWithoutNavbar>
        <Group isLoginPage={true} className='windows-onboarding-flow'>
          <Content isLoginPage={true}>
            <TopView>
              <Icon>
                <AppLogo height={60} />
              </Icon>
              <Title>Login</Title>
              <Form onSubmit={onSubmit} isLoginPage={true}>
                {!isReactNative() && DOMAIN_NAMES.includes(currentDomain) && (
                  <>
                    {/* google Button */}
                    <GoogleButton loading={loading} setLoading={setLoading} />
                    {/* OR Divider */}
                    <DividerRow>
                      <DividerLine />
                      <DividerText>or</DividerText>
                      <DividerLine />
                    </DividerRow>
                  </>
                )}
                {/* form section */}
                <FormSection>
                  <FormBlock>
                    <InputValue
                      type='email'
                      placeholder='Email Address'
                      value={username}
                      onChange={(e) => {
                        setEmail(e.target.value);
                      }}
                      isLoginPage={true}
                    />
                    <InputValue
                      type='password'
                      placeholder='Password'
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                      }}
                      className='password-input'
                      isLoginPage={true}
                    />
                  </FormBlock>
                  <FormViewLogin>
                    <Viewer role='button' onClick={() => history.push(ROUTES.FORGOT_PASSWORD)}>
                      Forgot Password?
                    </Viewer>
                    <Button
                      variant={BUTTON_TYPE.PRIMARY}
                      buttonType='submit'
                      onClick={() => onSubmit}
                      title='Login'
                      size={isMobile ? 'large' : 'medium'}
                      isLoading={loading}
                      width='100%'
                    />
                  </FormViewLogin>
                </FormSection>
              </Form>
            </TopView>
            <FormTextPoint>
              <FormText>Don’t have an account?</FormText>
              <Viewer role='button' onClick={() => history.push(ROUTES.SIGNUP)}>
                Create New Account
              </Viewer>
            </FormTextPoint>
          </Content>
        </Group>
      </LayoutWithoutNavbar>
    </>
  );
};

export default Login;
